import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useBackNavigation } from '@/shared/lib/navigation/BackNavigationContext';
import { useHapticFeedback } from '@/shared/hooks/useHapticFeedback';

interface SwipeBackContainerProps {
  children: ReactNode;
}

// Строгая зона левого края: 28px (только реальный безель экрана, чтобы не задевать кнопки и чекбоксы)
const EDGE_THRESHOLD = 28;
// Порог подтверждения свайпа: 115px (осознанное движение, исключающее случайные микро-рывки)
const SWIPE_SUCCESS_THRESHOLD = 115;

export function SwipeBackContainer({ children }: { children: ReactNode }) {
  const { canGoBack, triggerBack } = useBackNavigation();
  const { impact } = useHapticFeedback();

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<HTMLDivElement>(null);

  // Флаг: жест СТАРТОВАЛ именно в зоне левого края
  const isEdgeTouchRef = useRef(false);
  // Флаг: горизонтальное движение подтверждено и страница тащится
  const isSwipingRef = useRef(false);
  const startXRef = useRef(999999);
  const startYRef = useRef(999999);
  const currentDxRef = useRef(0);
  const startTimeRef = useRef(0);
  const canGoBackRef = useRef(canGoBack);
  canGoBackRef.current = canGoBack;

  const [chevronVisible, setChevronVisible] = useState(false);
  const [chevronActive, setChevronActive] = useState(false);
  const [chevronY, setChevronY] = useState(0);

  // Сброс всех инлайн-стилей трансформации: ВАЖНО для сохранения position: fixed
  // у дочерних элементов (BottomNav, плавающие кнопки). Если на предке висит
  // transform или will-change, браузер ломает viewport-позиционирование fixed элементов!
  const cleanupContentStyles = () => {
    if (contentRef.current) {
      contentRef.current.style.transform = '';
      contentRef.current.style.transition = '';
      contentRef.current.style.boxShadow = '';
      contentRef.current.style.willChange = '';
    }
    if (scrimRef.current) {
      scrimRef.current.style.display = 'none';
      scrimRef.current.style.opacity = '0';
      scrimRef.current.style.transition = '';
    }
  };

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      // КРИТИЧЕСКИ ВАЖНО: всегда обнуляем флаги при любом касании,
      // чтобы координаты предыдущего касания ни в коем случае не утекали в следующее!
      isEdgeTouchRef.current = false;
      isSwipingRef.current = false;
      startXRef.current = 999999;
      startYRef.current = 999999;
      currentDxRef.current = 0;

      if (e.touches.length !== 1) return;
      const touch = e.touches[0];

      // Проверяем: можно ли возвращаться назад и лежит ли точка касания у самого края
      if (!canGoBackRef.current) return;
      if (touch.clientX > EDGE_THRESHOLD) return;

      // Касание строго от левого края (0..28px)
      isEdgeTouchRef.current = true;
      startXRef.current = touch.clientX;
      startYRef.current = touch.clientY;
      startTimeRef.current = Date.now();
      setChevronY(touch.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Если касание не началось у самого левого края — немедленно игнорируем!
      if (!isEdgeTouchRef.current) return;
      if (e.touches.length !== 1) {
        isEdgeTouchRef.current = false;
        cleanupContentStyles();
        return;
      }

      const touch = e.touches[0];
      const dx = touch.clientX - startXRef.current;
      const dy = touch.clientY - startYRef.current;

      // Если жест ещё не перешёл в активную фазу сдвига:
      if (!isSwipingRef.current) {
        // Если пользователь двигает палец вертикально (скролл) — сразу отменяем жест края
        if (Math.abs(dy) > 10 && Math.abs(dy) >= dx) {
          isEdgeTouchRef.current = false;
          return;
        }

        // Для активации сдвига нужно чёткое горизонтальное движение вправо:
        // минимум 15px и строго преобладание горизонтали (в 2+ раза больше вертикали)
        if (dx >= 15 && dx >= Math.abs(dy) * 2.0) {
          isSwipingRef.current = true;
          setChevronVisible(true);
          if (contentRef.current) {
            contentRef.current.style.willChange = 'transform';
            contentRef.current.style.transition = 'none';
          }
          if (scrimRef.current) {
            scrimRef.current.style.transition = 'none';
            scrimRef.current.style.display = 'block';
          }
        }
      }

      // Активная фаза свайпа: страница следует за пальцем
      if (isSwipingRef.current) {
        if (e.cancelable) {
          e.preventDefault();
        }
        const clampedDx = Math.max(0, dx);
        currentDxRef.current = clampedDx;

        if (contentRef.current) {
          contentRef.current.style.transform = `translateX(${clampedDx}px)`;
          contentRef.current.style.boxShadow = '-12px 0 28px rgba(0, 0, 0, 0.4)';
        }

        if (scrimRef.current) {
          const progress = Math.min(1, clampedDx / (window.innerWidth * 0.8));
          scrimRef.current.style.opacity = (0.35 * (1 - progress)).toString();
        }

        const isPastThreshold = clampedDx >= SWIPE_SUCCESS_THRESHOLD;
        setChevronActive(isPastThreshold);
        setChevronY(touch.clientY);
      }
    };

    const handleTouchEnd = () => {
      const wasSwiping = isSwipingRef.current;
      const wasEdgeTouch = isEdgeTouchRef.current;

      // Обнуляем флаги
      isEdgeTouchRef.current = false;
      isSwipingRef.current = false;
      setChevronVisible(false);
      setChevronActive(false);

      if (!wasEdgeTouch || !wasSwiping) {
        cleanupContentStyles();
        return;
      }

      const dx = currentDxRef.current;
      const elapsed = Date.now() - startTimeRef.current;
      const velocity = dx / Math.max(1, elapsed);

      // Успех: сдвиг >= 115px ЛИБО быстрый осознанный флик (>= 70px и высокая скорость)
      const isSuccess = canGoBackRef.current && (dx >= SWIPE_SUCCESS_THRESHOLD || (dx >= 70 && velocity > 0.45));

      if (isSuccess) {
        // Успешный свайп: анимируем уезд экрана вправо и вызываем возврат
        impact('light');
        if (contentRef.current) {
          contentRef.current.style.transition = 'transform 0.18s cubic-bezier(0.2, 0.9, 0.3, 1)';
          contentRef.current.style.transform = `translateX(${window.innerWidth + 20}px)`;
        }
        if (scrimRef.current) {
          scrimRef.current.style.transition = 'opacity 0.18s ease';
          scrimRef.current.style.opacity = '0';
        }

        setTimeout(() => {
          cleanupContentStyles();
          triggerBack();
        }, 180);
      } else {
        // Отмена: плавно возвращаем экран на место
        if (contentRef.current) {
          contentRef.current.style.transition = 'transform 0.22s cubic-bezier(0.2, 0.9, 0.3, 1), box-shadow 0.22s ease';
          contentRef.current.style.transform = 'translateX(0px)';
          contentRef.current.style.boxShadow = 'none';
        }
        if (scrimRef.current) {
          scrimRef.current.style.transition = 'opacity 0.22s ease';
          scrimRef.current.style.opacity = '0';
        }
        setTimeout(() => {
          cleanupContentStyles();
        }, 220);
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [triggerBack, impact]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      }}
    >
      {/* Затемняющий оверлей снизу при свайпе */}
      <div
        ref={scrimRef}
        style={{
          display: 'none',
          position: 'fixed',
          inset: 0,
          background: '#000',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: 998,
        }}
      />

      {/* Плавающий нативный iOS шеврон возврата */}
      {chevronVisible && (
        <div
          ref={chevronRef}
          style={{
            position: 'fixed',
            left: 12,
            top: chevronY || '50%',
            transform: `translateY(-50%) scale(${chevronActive ? 1.15 : 0.9})`,
            width: 36,
            height: 36,
            borderRadius: 18,
            background: chevronActive ? 'var(--theme-primary, #3b82f6)' : 'rgba(20, 25, 40, 0.85)',
            border: `1px solid ${chevronActive ? 'var(--theme-primary, #3b82f6)' : 'rgba(255, 255, 255, 0.2)'}`,
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 999,
            pointerEvents: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            transition: 'background 0.15s, transform 0.15s, border-color 0.15s',
            backdropFilter: 'blur(8px)',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
      )}

      {/* Контейнер текущей страницы */}
      <div
        ref={contentRef}
        style={{
          width: '100%',
          minHeight: '100dvh',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          background: 'var(--theme-bg)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
